import { FC, useCallback, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import styled from "styled-components";

import { Shelf } from "capitalrx-components-2.0/lib/layout";
import { Menu, Tooltip } from "capitalrx-components-2.0/lib/general";

import { LanguageSwitcher } from "@/App/components/LanguageSwitcher";
import { ThemedTopNavButton } from "@/App/components";
import { AuthenticationContext, ExperienceContext, MemberEligibilityContext } from "@/App/context";
import { ANALYTIC_EVENTS, logEvent } from "@/App/service";
import { useNavigateClient } from "@/App/hooks";
import { ClientAwareLink } from "@/App/components";

import { MenuItem } from "../MenuBar";
import { ThemedLink } from "../../themed";

const ClientAwareMenuLink = styled(ClientAwareLink)`
  font-size: 16px;

  color: #313e48 !important;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const MenuItemShelf = styled(Shelf)`
  min-width: 180px;
  padding-top: 12px;
  padding-bottom: 12px;
`;

const TopNav = styled(Shelf)`
  background-color: ${(props) => props.theme?.primary_color};
  padding: 12px;
  height: 44px;
`;

export const MenuLink = styled(ThemedLink)`
  font-size: 16px;

  color: #313e48 !important;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

export const MobileTopBar: FC = () => {
  const { loginRedirectUrl, hideInboxPage, hideFormDocumentsPage, hidePreferences } = useContext(ExperienceContext);
  const { t: translate } = useTranslation();
  const { logoutUser } = useContext(AuthenticationContext);
  const { loggedInEligibility } = useContext(MemberEligibilityContext);
  const navigateClient = useNavigateClient();
  const navigate = useNavigate();

  const accountMenuItems = useMemo(() => {
    const account: MenuItem[] = [];
    if (!hideInboxPage) {
      account.push({
        displayText: translate("MENU_BAR.inbox"),
        to: "/inbox",
        analyticsEvent: ANALYTIC_EVENTS.INBOX.topNav,
      });
    }
    if (!hideFormDocumentsPage) {
      account.push({
        to: "/forms-and-documents",
        displayText: translate("MENU_BAR.formsAndDocuments"),
        analyticsEvent: ANALYTIC_EVENTS.FORMS_AND_DOCUMENTS.topNav,
      });
    }
    if (!hidePreferences) {
      account.push({
        displayText: translate("MENU_BAR.preferences"),
        to: "/notifications",
        analyticsEvent: ANALYTIC_EVENTS.PRFERENCES.topNav,
      });
    }
    return account;
  }, [hideInboxPage, hideFormDocumentsPage, hidePreferences]);

  const onCommonMenuItemClick = useCallback((menuItem: MenuItem) => {
    if (menuItem.analyticsEvent) {
      if (menuItem.analyticsParams) {
        logEvent(menuItem.analyticsEvent, menuItem.analyticsParams);
      } else {
        logEvent(menuItem.analyticsEvent);
      }
    }
  }, []);

  const signOut = useCallback(async () => {
    logEvent(ANALYTIC_EVENTS.SIGN_OUT_BUTTON);
    await logoutUser();
    if (loginRedirectUrl) {
      navigate(loginRedirectUrl);
    } else {
      navigateClient("/login");
    }
  }, [logoutUser, loginRedirectUrl]);

  const firstName = loggedInEligibility.member.first_name;
  const lastName = loggedInEligibility.member.last_name;

  let accountName = translate("MENU_BAR.account") + ": " + firstName;

  if(firstName && lastName) {
    accountName = accountName + " " + lastName;
  }

  return (
    <TopNav spacing="large">
      <LanguageSwitcher />
      <Shelf.Spacer />
      <ThemedTopNavButton
        dynamicCaret={true}
        appearance="text"
        icon="user"
        label={
          <Tooltip content={accountName}>
            {(ref) => (
              <span ref={ref} className="overflow-ellipsis">{accountName}</span>
            )}
          </Tooltip>
        }>
        <Menu.Group>
          {accountMenuItems.map((menuItem, idx) => {
            return (
              <Menu.ComponentItem key={menuItem.to + idx.toString()}>
                <MenuItemShelf>
                  {menuItem.openBehavior === "externalPage" && (
                    <MenuLink onClick={() => onCommonMenuItemClick(menuItem)} to={menuItem.to}>
                      {menuItem.displayText}
                    </MenuLink>
                  )}
                  {!menuItem.openBehavior && (
                    <ClientAwareMenuLink analyticsEvent={menuItem.analyticsEvent} to={menuItem.to}>
                      {menuItem.displayText}
                    </ClientAwareMenuLink>
                  )}
                  <Shelf.Spacer />
                </MenuItemShelf>
              </Menu.ComponentItem>
            );
          })}
        </Menu.Group>
        <Menu.Group>
          <Menu.ComponentItem>
            <MenuItemShelf>
              <MenuLink to="/login" onClick={signOut}>
                {translate("MENU_BAR.signOut")}
              </MenuLink>
              <Shelf.Spacer />
            </MenuItemShelf>
          </Menu.ComponentItem>
        </Menu.Group>
      </ThemedTopNavButton>
    </TopNav>
  );
};
